import { api } from "../api";

/**
 * One-shot, module-cached read of this app's Shopify app handle.
 *
 * WHY THIS EXISTS RATHER THAN `useGlobalAction(api.getHandle)` IN THE ROUTE:
 * routes/billing.jsx used to fire `getHandle()` from a MOUNT EFFECT, so the round
 * trip could not start until the billing route chunk had downloaded, parsed and
 * committed. Calling `primeAppHandle()` from the route's `lazy()` factory in
 * components/App.jsx instead starts it the moment the route is WANTED, overlapping
 * it with the chunk download. Same trick routes/popup.jsx uses for `chunks_ready`.
 *
 * ⚠️ IT NEVER REJECTS, by construction. It is called from a `lazy()` factory, and a
 * rejection escaping there would fail the whole route chunk load. Errors are
 * resolved as `{ handle: null, error }` so the caller can render its "couldn't load
 * plan details" banner — exactly what the previous `handleError` state did.
 *
 * ⚠️ Module-cached on purpose: `getHandle` takes no arguments and its result never
 * changes for the life of the app (see api/actions/getHandle.js), so repeat callers
 * — a second mount, StrictMode's double effect invoke — share the one in-flight or
 * already-settled promise instead of re-requesting.
 *
 * @returns {Promise<{ handle: string | null, error: unknown }>}
 */
let handlePromise = null;

export function primeAppHandle() {
  if (!handlePromise) {
    handlePromise = api
      .getHandle()
      .then((handle) => ({ handle: handle ?? null, error: null }))
      .catch((error) => ({ handle: null, error }));
  }
  return handlePromise;
}
